import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import {
  Rarity,
  So5FixtureEvent,
} from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Gain } from '@sorare/core/src/components/scoring/Gain';
import { Score } from '@sorare/core/src/components/scoring/Score';
import type { SignedScoreVariant } from '@sorare/core/src/components/scoring/SignedScore';
import { SignedScore } from '@sorare/core/src/components/scoring/SignedScore';
import { isTypeFn } from '@sorare/core/src/lib/gql';

import { AnyStatsIconCell } from '@sorare/marketplace/src/components/searchPlayerTable/SearchPlayerCell/AnyStatsIconCell';
import { Cell } from '@sorare/marketplace/src/components/searchPlayerTable/SearchTable/ui';
import { StartingPriceCell } from '@sorare/marketplace/src/components/searchPlayerTable/StartingPriceCell';
import { PlayerRotowireStatsProjection } from '@sorare/marketplace/src/shared/player/PlayerDetails/PlayerStats/PlayerRotowireStatsProjection';
import { InlineGame } from '@sorare/marketplace/src/shared/stats/InlineGame';

import type { PlayerScoreView } from '../../types';
import {
  AnySportPlayerScoreStats,
  NBAPlayerScoreStats,
  PlayerScoreViewAttributes,
} from '../../types';
import type { FixtureTopPlayersTableRow_fixturePlayer } from '../__generated__/index.graphql';
import type { NBAScoreCells_fixturePlayer } from './__generated__/index.graphql';

const ScoreCells = styled.div`
  display: grid;
  grid-template-columns: subgrid;
  padding: var(--unit) 0;
  row-gap: var(--unit);
`;

type NBAScoreCellsProps = {
  eligibleSo5Scores: FixtureTopPlayersTableRow_fixturePlayer['eligibleSo5Scores'];
  statsManager: PlayerScoreView;
  isUpcomingFixture?: boolean;
  player: FixtureTopPlayersTableRow_fixturePlayer['player'];
  onListingsClick: () => void;
  eventType?: So5FixtureEvent;
};

export const NBAScoreCells = ({
  eligibleSo5Scores,
  statsManager,
  isUpcomingFixture,
  player,
  onListingsClick,
  eventType,
}: NBAScoreCellsProps) => {
  const eligibleNbaScores = eligibleSo5Scores.filter(
    isTypeFn('BasketballPlayerGameScore')
  );

  const scoreToDisplay = eligibleNbaScores.reduce((highest, current) =>
    (current.score ?? 0) > (highest.score ?? 0) ? current : highest
  );

  const gridColumnStartIndex = 2;

  const signedScoreVariant: Partial<
    Record<NBAPlayerScoreStats, SignedScoreVariant>
  > = {
    [NBAPlayerScoreStats.MIN]: 'neutral',
    [NBAPlayerScoreStats.TO]: 'negative',
  };

  const { score, scoreStatus, basketballPlayerGameStats } =
    scoreToDisplay || {};
  const {
    points,
    rebounds,
    assists,
    blocks,
    steals,
    turnovers,
    threePointsMade,
    minsPlayed,
  } = basketballPlayerGameStats || {};

  const gainOnProjection =
    eventType === So5FixtureEvent.DAILY
      ? player.gainOnDailyProjection
      : player.gainOnClassicProjection;

  const playerScoreStats: {
    [key in AnySportPlayerScoreStats | NBAPlayerScoreStats]?: ReactNode;
  } = isUpcomingFixture
    ? {
        [AnySportPlayerScoreStats.PROJECTED_SCORE]: score,
        [AnySportPlayerScoreStats.GAIN_ON_PROJECTION]: gainOnProjection,
        [NBAPlayerScoreStats.L10]: player.L10 || 0,
        [NBAPlayerScoreStats.DIFF]: score - (player.L10 || 0) || 0,
        [NBAPlayerScoreStats.GAMES]: (
          <Vertical>
            {eligibleSo5Scores.map(
              ({ anyGame }) =>
                anyGame && (
                  <InlineGame
                    key={anyGame.id}
                    game={anyGame}
                    withTeamCode
                    withGameStatus
                  />
                )
            )}
          </Vertical>
        ),
        [AnySportPlayerScoreStats.FORM]: (
          <AnyStatsIconCell
            showOnMobile
            anyPlayer={player}
            onStatsClick={() => null}
          />
        ),
        [AnySportPlayerScoreStats.PRICE]: (
          <StartingPriceCell
            rarity={Rarity.limited}
            anyPlayer={player}
            onListingsClick={onListingsClick}
          />
        ),
      }
    : {
        [AnySportPlayerScoreStats.SCORE]: score,
        [NBAPlayerScoreStats.L10]: player.L10 || 0,
        [NBAPlayerScoreStats.DIFF]: score - (player.L10 || 0) || 0,
        [NBAPlayerScoreStats.PTS]: points || 0,
        [NBAPlayerScoreStats.REB]: rebounds || 0,
        [NBAPlayerScoreStats.AST]: assists || 0,
        [NBAPlayerScoreStats.BLK]: blocks || 0,
        [NBAPlayerScoreStats.STL]: steals || 0,
        [NBAPlayerScoreStats.TO]: turnovers || 0,
        [NBAPlayerScoreStats.THREE_PT]: threePointsMade || 0,
        [NBAPlayerScoreStats.MIN]: minsPlayed || 0,
      };

  const nbaStats = statsManager[
    PlayerScoreViewAttributes.PLAYER_SCORE
  ] as ReadonlyArray<NBAPlayerScoreStats | AnySportPlayerScoreStats>;

  return (
    <ScoreCells
      style={{
        gridColumn: `${gridColumnStartIndex} / ${statsManager[PlayerScoreViewAttributes.PLAYER_SCORE].length + gridColumnStartIndex}`,
      }}
    >
      {nbaStats.map(stat => {
        return (
          <Cell key={stat}>
            {typeof playerScoreStats[stat] === 'number' ? (
              <>
                {isUpcomingFixture ? (
                  <>
                    {isUpcomingFixture ? (
                      <>
                        {stat === AnySportPlayerScoreStats.PROJECTED_SCORE &&
                          !!scoreStatus && (
                            <PlayerRotowireStatsProjection
                              player={player}
                              positions={player.anyPositions}
                              playerGameScores={[scoreToDisplay]}
                              variant="hexagon"
                            />
                          )}

                        {stat === AnySportPlayerScoreStats.GAIN_ON_PROJECTION &&
                          !!scoreStatus && (
                            <Gain value={gainOnProjection} size={4} />
                          )}
                        {[NBAPlayerScoreStats.L10].includesValue(stat) &&
                          !!scoreStatus && (
                            <Score
                              hideReviewing
                              shape="HEXAGON"
                              score={playerScoreStats[stat]}
                              scoreStatus="FINAL"
                              size={4}
                            />
                          )}
                      </>
                    ) : (
                      <>
                        {[
                          AnySportPlayerScoreStats.SCORE,
                          NBAPlayerScoreStats.L10,
                        ].includesValue(stat) &&
                          !!scoreStatus && (
                            <Score
                              hideReviewing
                              shape={
                                NBAPlayerScoreStats.L10 === stat
                                  ? 'HEXAGON'
                                  : 'PILL'
                              }
                              score={playerScoreStats[stat]}
                              scoreStatus={
                                NBAPlayerScoreStats.L10 === stat
                                  ? 'FINAL'
                                  : scoreStatus
                              }
                              scoreType="PLAYER_SCORE"
                              size={4}
                            />
                          )}
                        {![
                          AnySportPlayerScoreStats.SCORE,
                          NBAPlayerScoreStats.L10,
                        ].includesValue(stat) &&
                          !!scoreStatus && (
                            <SignedScore
                              variant={signedScoreVariant[stat]}
                              score={playerScoreStats[stat] || 0}
                              signed={stat === NBAPlayerScoreStats.DIFF}
                            />
                          )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {[
                      AnySportPlayerScoreStats.SCORE,
                      NBAPlayerScoreStats.L10,
                    ].includesValue(stat) &&
                      !!scoreStatus && (
                        <Score
                          hideReviewing
                          shape={
                            NBAPlayerScoreStats.L10 === stat
                              ? 'HEXAGON'
                              : 'PILL'
                          }
                          score={playerScoreStats[stat]}
                          scoreStatus={
                            NBAPlayerScoreStats.L10 === stat
                              ? 'FINAL'
                              : scoreStatus
                          }
                          scoreType="PLAYER_SCORE"
                          size={4}
                        />
                      )}
                    {![
                      AnySportPlayerScoreStats.SCORE,
                      NBAPlayerScoreStats.L10,
                    ].includesValue(stat) &&
                      !!scoreStatus && (
                        <SignedScore
                          variant={signedScoreVariant[stat]}
                          score={playerScoreStats[stat] || 0}
                          signed={stat === NBAPlayerScoreStats.DIFF}
                        />
                      )}
                  </>
                )}
              </>
            ) : (
              playerScoreStats[stat]
            )}
          </Cell>
        );
      })}
    </ScoreCells>
  );
};

NBAScoreCells.fragments = {
  fixturePlayer: gql`
    fragment NBAScoreCells_fixturePlayer on FixturePlayer {
      id
      score
      eligibleSo5Scores {
        id
        score
        scoreStatus
        anyGame {
          id
          ...InlineGame_game
        }
        ... on BasketballPlayerGameScore {
          id
          ...PlayerRotowireStatsProjection_PlayerGameScoreInterface
          basketballPlayerGameStats {
            id
            points
            rebounds
            assists
            blocks
            steals
            turnovers
            threePointsMade
            minsPlayed
          }
        }
      }
      player {
        slug
        L10: averageScore(type: LAST_TEN_PLAYED_SO5_AVERAGE_SCORE)
        gainOnClassicProjection
        gainOnDailyProjection
        ...AnyStatsIconCell_anyPlayer
        ...StartingPriceCell_anyPlayer
        ...PlayerRotowireStatsProjection_AnyPlayerInterface
      }
    }
    ${InlineGame.fragments.game}
    ${AnyStatsIconCell.fragments.anyPlayer}
    ${StartingPriceCell.fragments.anyPlayer}
    ${PlayerRotowireStatsProjection.fragments.anyPlayerInterface}
    ${PlayerRotowireStatsProjection.fragments.playerGameScoreInterface}
  ` as TypedDocumentNode<NBAScoreCells_fixturePlayer>,
};
